import {
  EocStudyPlanSectionType,
  Program,
  newPlanInputFormProperties,
  newSectionItemProperties,
  refScope,
  EocStudyPlanActivationInheritMode,
  newSectionInputFormProperties,
  eocDraftPlanResponse,
  eocCancelDraftPlanResponse,
  eocActivateDraftPlanResponse,
  updatePlanInputFormProperties,
  eocDraftPlanItemModifyResponse,
  updateSectionItemProperties,
  ResetRestoreMasteryLessonsRequest,
  ResetWorkkeysQtiTestRequest,
  ResetWorkkeysQtiTestResponse,
  SetGlobalStudentSettingsInput
} from "../../../types/gqlTypes/graphql"
import {
  ACTIVATE_PLAN,
  ADD_SECTION,
  ADD_SECTION_ITEM,
  ASSIGN_CLASS_SUBJECT,
  ASSIGN_STUDENT_CLASS,
  CANCEL_PLAN,
  CHANGE_TO_ADAPTIVE_PLAN,
  CHANGE_TO_MANUAL_PLAN,
  CLEAR_BREAK_AFTER_ITEM,
  CLEAR_BREAK_AFTER_SECTION,
  CLEAR_BREAK_BEFORE_ITEM,
  CLEAR_BREAK_BEFORE_SECTION,
  CLEAR_ITEM_COMPLETED,
  CLEAR_ITEM_TEACH_LIVE,
  MARK_BREAK_AFTER_ITEM,
  MARK_BREAK_AFTER_SECTION,
  MARK_BREAK_BEFORE_ITEM,
  MARK_BREAK_BEFORE_SECTION,
  MARK_ITEM_COMPLETED,
  MARK_ITEM_TEACH_LIVE,
  MOVE_ITEM_TO_NEW_SECTION,
  NEW_PLAN,
  REDO,
  REMOVE_SECTION,
  REMOVE_SECTION_ITEM,
  REORDER_ITEM,
  REORDER_SECTION,
  REPLACE_SECTION_ITEM,
  RESET_MASTERY_LESSONS,
  RESET_WORKKEYS_QTI_TEST,
  RESTORE_MASTERY_LESSONS,
  SET_GLOBAL_STUDENT_SETTINGS,
  SWITCH_TO_RECOMMENDED_PLAN,
  UNDO,
  UPDATE_PLAN,
  UPDATE_SECTION,
  UPDATE_SECTION_ITEM
} from "./studyPlanMutations"

import { executeMutationSideload } from "../../generic-services/helpers/studyPlan"

// Resolver -> function to do the gql query and simply return the data. There's no post processing of the data in these functions
// NOTE: This file use a lot of types pre-defined in graphql.d.ts which copy from studyPlan backend

export async function redo(planId: String): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(REDO, { plan_id: planId })
  return response.redo
}
export async function undo(planId: String): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(UNDO, { plan_id: planId })
  return response.undo
}

export async function newPlan(
  program: Program,
  subject: string,
  refScope: refScope,
  properties: newPlanInputFormProperties
): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(NEW_PLAN, {
    program,
    subject,
    refScope,
    properties
  })
  return response.newPlan
}

export async function updatePlan(
  planId: string,
  properties: updatePlanInputFormProperties
): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(UPDATE_PLAN, {
    plan_id: planId,
    properties
  })
  return response.updatePlan
}

export async function addSection(
  planId: string,
  sectionType: EocStudyPlanSectionType,
  targetSeconds?: number
): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(ADD_SECTION, {
    plan_id: planId,
    section_type: sectionType,
    properties: {
      targetSeconds: targetSeconds ?? 0
    }
    // assume default is 5 hours ?
  })
  // console.log("type: " + sectionType+" target_seconds: " + target_seconds)
  return response.addSection
}
export async function updateSection(
  planId: string,
  sectionId: string,
  properties: newSectionInputFormProperties
): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(UPDATE_SECTION, {
    plan_id: planId,
    section_id: sectionId,
    properties
  })
  return response.updateSection
}

export async function addSectionItem(
  planId: string,
  sectionId: string,
  contentId: string,
  properties: newSectionItemProperties
): Promise<eocDraftPlanItemModifyResponse> {
  const response = await executeMutationSideload(ADD_SECTION_ITEM, {
    plan_id: planId,
    section_id: sectionId,
    content_id: contentId,
    properties: {
      breakAfter: properties?.breakAfter ?? false,
      breakBefore: properties?.breakBefore ?? false,
      isTeachLive: properties?.isTeachLive ?? false
    }
  })
  return response.addSectionItem
}

export async function updateSectionItem(
  planId: string,
  sectionId: string,
  itemId: string,
  properties: updateSectionItemProperties
): Promise<eocDraftPlanItemModifyResponse> {
  const response = await executeMutationSideload(UPDATE_SECTION_ITEM, {
    plan_id: planId,
    section_id: sectionId,
    item_id: itemId,
    properties
  })
  return response.updateSectionItem
}

export async function replaceSectionItem(
  planId: string,
  sectionId: string,
  contentId: string,
  properties: newSectionItemProperties
): Promise<eocDraftPlanItemModifyResponse> {
  const response = await executeMutationSideload(REPLACE_SECTION_ITEM, {
    plan_id: planId,
    section_id: sectionId,
    content_id: contentId,
    properties: {
      breakAfter: properties?.breakAfter ?? false,
      breakBefore: properties?.breakBefore ?? false,
      isTeachLive: properties?.isTeachLive ?? false
    }
  })
  // backend renamed the function
  return response.resetPracticeTestSectionWithItem
}

export async function reorderSectionItem(
  planId: string,
  sectionId: string,
  itemId: string,
  index: number,
  toSectionId?: string
): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(REORDER_ITEM, {
    plan_id: planId,
    section_id: sectionId,
    item_id: itemId,
    index,
    to_section_id: toSectionId
  })
  return response.reorderItem
}

export async function reorderSection(
  planId: string,
  sectionId: string,
  index: number
): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(REORDER_SECTION, {
    plan_id: planId,
    section_id: sectionId,
    index
  })
  return response.reorderSection
}

export async function removeSectionItem(
  planId: string,
  sectionId: string,
  itemId: string
): Promise<eocDraftPlanItemModifyResponse> {
  const response = await executeMutationSideload(REMOVE_SECTION_ITEM, {
    plan_id: planId,
    section_id: sectionId,
    item_id: itemId
  })
  return response.removeSectionItem
}

export async function moveItemToNewSection(
  planId: string,
  sectionId: string,
  itemId: string,
  newSectionIndex?: number
): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(MOVE_ITEM_TO_NEW_SECTION, {
    plan_id: planId,
    section_id: sectionId,
    item_id: itemId,
    new_section_index: newSectionIndex
  })
  return response.moveItemToNewSection
}

export async function removeSection(
  planId: string,
  sectionId: string
): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(REMOVE_SECTION, {
    plan_id: planId,
    section_id: sectionId
  })
  return response.removeSection
}

export async function cancelPlan(
  planId: string
): Promise<eocCancelDraftPlanResponse> {
  const response = await executeMutationSideload(CANCEL_PLAN, {
    plan_id: planId
  })
  return response.cancelPlan
}

export async function activatePlan(
  planId: string,
  overwriteMode: EocStudyPlanActivationInheritMode
): Promise<eocActivateDraftPlanResponse> {
  const response = await executeMutationSideload(ACTIVATE_PLAN, {
    plan_id: planId,
    overwriteMode // no case change?
  })
  return response.activatePlan
}

export async function changeToAdaptivePlan(
  planId: string,
  refScope: refScope
): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(CHANGE_TO_ADAPTIVE_PLAN, {
    plan_id: planId,
    refScope
  })
  return response.changeToAdaptivePlan
}

export async function changeToManualPlan(
  planId: string,
  refScope: refScope
): Promise<eocDraftPlanResponse> {
  const response = await executeMutationSideload(CHANGE_TO_MANUAL_PLAN, {
    plan_id: planId,
    refScope
  })
  return response.changeToManualPlan
}

export async function switchToRecommendedPlan(
  program: Program,
  subject: string,
  refScope: refScope
): Promise<boolean> {
  const response = await executeMutationSideload(SWITCH_TO_RECOMMENDED_PLAN, {
    program,
    subject,
    refScope
  })
  return response.switchToRecommendedPlan
}

export async function markBreakBeforeItem(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  itemGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(MARK_BREAK_BEFORE_ITEM, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    item_guid: itemGuid
  })
  return response.markBreakBeforeItem
}

export async function markBreakBeforeSection(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  sectionGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(MARK_BREAK_BEFORE_SECTION, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    section_guid: sectionGuid
  })
  return response.markBreakBeforeSection
}

export async function markBreakAfterSection(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  sectionGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(MARK_BREAK_AFTER_SECTION, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    section_guid: sectionGuid
  })
  return response.markBreakAfterSection
}

export async function clearBreakBeforeSection(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  sectionGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(CLEAR_BREAK_BEFORE_SECTION, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    section_guid: sectionGuid
  })
  return response.clearBreakBeforeSection
}

export async function clearBreakAfterSection(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  sectionGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(CLEAR_BREAK_AFTER_SECTION, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    section_guid: sectionGuid
  })
  return response.clearBreakAfterSection
}

export async function markBreakAfterItem(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  itemGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(MARK_BREAK_AFTER_ITEM, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    item_guid: itemGuid
  })
  return response.markBreakAfterItem
}

export async function clearBreakBeforeItem(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  itemGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(CLEAR_BREAK_BEFORE_ITEM, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    item_guid: itemGuid
  })
  return response.clearBreakBeforeItem
}

export async function clearBreakAfterItem(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  itemGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(CLEAR_BREAK_AFTER_ITEM, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    item_guid: itemGuid
  })
  return response.clearBreakAfterItem
}

export async function markItemTeachLive(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  itemGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(MARK_ITEM_TEACH_LIVE, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    item_guid: itemGuid
  })
  return response.markItemTeachLive
}

export async function clearItemTeachLive(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  itemGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(CLEAR_ITEM_TEACH_LIVE, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    item_guid: itemGuid
  })
  return response.clearItemTeachLive
}

export async function markItemCompleted(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  itemGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(MARK_ITEM_COMPLETED, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    item_guid: itemGuid
  })
  return response.markItemCompleted
}

export async function clearItemCompleted(
  program: Program,
  subject: string,
  refScope: refScope,
  planGuid: string,
  itemGuid: string
): Promise<boolean> {
  const response = await executeMutationSideload(CLEAR_ITEM_COMPLETED, {
    program,
    subject,
    refScope,
    plan_guid: planGuid,
    item_guid: itemGuid
  })
  return response.clearItemCompleted
}

export async function assignClassSubject(
  program: Program,
  subject: string,
  classId: string
): Promise<boolean> {
  const response = await executeMutationSideload(ASSIGN_CLASS_SUBJECT, {
    program,
    subject,
    class_id: classId
  })
  return response.assignClassSubject
}

export async function assignStudentClass(
  program: Program,
  subject: string,
  classId: string,
  studentId: string
): Promise<boolean> {
  const response = await executeMutationSideload(ASSIGN_STUDENT_CLASS, {
    program,
    subject,
    class_id: classId,
    student_id: studentId
  })
  return response.assignStudentClass
}

export async function setGlobalStudentSettings(
  data: SetGlobalStudentSettingsInput
): Promise<string> {
  const response = await executeMutationSideload(SET_GLOBAL_STUDENT_SETTINGS, {
    data
  })
  return response.setGlobalStudentSettings
}

export interface ResetResult {
  response: boolean
  contentId: string
  message?: any
  allowUndo?: boolean
}

export async function resetMasteryLessons(
  data: ResetRestoreMasteryLessonsRequest,
  allowUndo = true
): Promise<ResetResult> {
  const response = await executeMutationSideload(RESET_MASTERY_LESSONS, {
    data
  })
  return {
    response: response.resetMasteryLessons,
    contentId: data.contentId,
    allowUndo
  }
}

export async function restoreMasteryLessons(
  data: ResetRestoreMasteryLessonsRequest,
  allowUndo = true
): Promise<ResetResult> {
  const response = await executeMutationSideload(RESTORE_MASTERY_LESSONS, {
    data
  })
  return {
    response: response.restoreMasteryLessons,
    contentId: data.contentId,
    allowUndo
  }
}

export async function resetWorkkeysQtiTest(
  data: ResetWorkkeysQtiTestRequest
): Promise<ResetWorkkeysQtiTestResponse> {
  const response = await executeMutationSideload(RESET_WORKKEYS_QTI_TEST, {
    data
  })
  return response.resetWorkkeysQtiTest
}

// DF Disabled until further notice
// export async function markGuidStudentCanDoOutOfOrder(
//   data: PlanGuidMetaInput
// ): Promise<boolean> {
//   const response = await executeMutationSideload(
//     MARK_GUID_STUDENT_CAN_DO_OUT_OF_ORDER,
//     {
//       data
//     }
//   )
//   return response.markGuidStudentCanDoOutOfOrder
// }
// export async function clearGuidStudentCanDoOutOfOrder(
//   data: PlanGuidMetaInput
// ): Promise<boolean> {
//   const response = await executeMutationSideload(
//     CLEAR_GUID_STUDENT_CAN_DO_OUT_OF_ORDER,
//     {
//       data
//     }
//   )
//   return response.clearGuidStudentCanDoOutOfOrder
// }
// export async function markGuidStudentCanResetMasteryLessons(
//   data: PlanGuidMetaInput
// ): Promise<boolean> {
//   const response = await executeMutationSideload(
//     MARK_GUID_STUDENT_CAN_RESET_MASTERY_LESSONS,
//     {
//       data
//     }
//   )
//   return response.markGuidStudentCanResetMasteryLessons
// }
// export async function clearGuidStudentCanResetMasteryLessons(
//   data: PlanGuidMetaInput
// ): Promise<boolean> {
//   const response = await executeMutationSideload(
//     CLEAR_GUID_STUDENT_CAN_RESET_MASTERY_LESSONS,
//     {
//       data
//     }
//   )
//   return response.clearGuidStudentCanResetMasteryLessons
// }
